// Register Page with Domain-Based Tenant Suggestion

import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../stores/auth-store';
import { trpc } from '../../lib/trpc';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  displayName: string;
  tenantName: string;
}

interface SuggestedTenant {
  id: string;
  name: string;
  memberCount: number;
}

type RegistrationMode = 'register' | 'request-access';

export function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  // State for domain-based suggestions
  const [mode, setMode] = useState<RegistrationMode>('register');
  const [suggestedTenants, setSuggestedTenants] = useState<SuggestedTenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<SuggestedTenant | null>(null);
  const [accessMessage, setAccessMessage] = useState('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    getValues,
  } = useForm<RegisterFormData>();

  const password = watch('password');
  const email = watch('email');

  // Check email domain mutation
  const checkEmailDomainMutation = trpc.auth.checkEmailDomain.useMutation({
    onSuccess: (data) => {
      setCheckingEmail(false);
      setEmailChecked(true);
      if (data.suggestedTenants.length > 0) {
        setSuggestedTenants(data.suggestedTenants);
      } else {
        setSuggestedTenants([]);
      }
    },
    onError: () => {
      setCheckingEmail(false);
      setEmailChecked(true);
      setSuggestedTenants([]);
    },
  });

  // Register mutation
  const registerMutation = trpc.auth.register.useMutation({
    onSuccess: (data) => {
      login(data.accessToken, data.refreshToken, data.user, data.expiresAt);
      navigate('/dashboard');
    },
    onError: (error) => {
      setError('root', { message: error.message });
    },
  });

  // Request access mutation
  const requestAccessMutation = trpc.auth.requestAccess.useMutation({
    onSuccess: () => {
      // Show success message and redirect to login
      navigate('/login', {
        state: {
          message: 'Your access request has been submitted. You will receive an email when the administrator reviews your request.',
        },
      });
    },
    onError: (error) => {
      setError('root', { message: error.message });
    },
  });

  // Check email domain on blur
  const handleEmailBlur = useCallback(() => {
    const emailValue = getValues('email');
    if (emailValue && emailValue.includes('@')) {
      setCheckingEmail(true);
      setEmailChecked(false);
      setSuggestedTenants([]);
      setSelectedTenant(null);
      setMode('register');
      checkEmailDomainMutation.mutate({ email: emailValue });
    }
  }, [getValues, checkEmailDomainMutation]);

  // Handle registration form submit
  const onSubmit = (data: RegisterFormData) => {
    if (mode === 'request-access' && selectedTenant) {
      requestAccessMutation.mutate({
        email: data.email,
        tenantId: selectedTenant.id,
        message: accessMessage || undefined,
      });
    } else {
      registerMutation.mutate({
        email: data.email,
        password: data.password,
        displayName: data.displayName,
        tenantName: data.tenantName,
      });
    }
  };

  // Switch to request access mode
  const handleSelectTenant = (tenant: SuggestedTenant) => {
    setSelectedTenant(tenant);
    setMode('request-access');
    clearErrors('root');
  };

  // Switch back to register mode
  const handleCreateNewOrg = () => {
    setSelectedTenant(null);
    setMode('register');
    clearErrors('root');
  };

  const isPending = registerMutation.isPending || requestAccessMutation.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <img src="/calculom.png" alt="Calculom" className="w-[110px] h-auto mx-auto mb-4" />
          <p className="text-gray-600">
            {mode === 'request-access' ? 'Request access to an organization' : 'Create your account'}
          </p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {errors.root && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                {errors.root.message}
              </div>
            )}

            {/* Email field - always visible */}
            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="input"
                  placeholder="john@acme.com"
                  {...register('email', { required: 'Email is required' })}
                  onBlur={handleEmailBlur}
                />
                {checkingEmail && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg className="h-5 w-5 animate-spin text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Suggested tenants - show after email check */}
            {emailChecked && suggestedTenants.length > 0 && mode === 'register' && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-800 mb-3">
                  We found existing organizations with your email domain:
                </p>
                <div className="space-y-2">
                  {suggestedTenants.map((tenant) => (
                    <button
                      key={tenant.id}
                      type="button"
                      onClick={() => handleSelectTenant(tenant)}
                      className="w-full text-left rounded-lg border border-blue-200 bg-white p-3 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      <div className="font-medium text-gray-900">{tenant.name}</div>
                      <div className="text-sm text-gray-500">
                        {tenant.memberCount} {tenant.memberCount === 1 ? 'member' : 'members'}
                      </div>
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-blue-600">
                  Or continue below to create a new organization
                </p>
              </div>
            )}

            {/* Request Access Mode */}
            {mode === 'request-access' && selectedTenant && (
              <>
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Requesting access to:
                      </p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">
                        {selectedTenant.name}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCreateNewOrg}
                      className="text-sm text-green-700 hover:text-green-800 underline"
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="accessMessage" className="label">
                    Message to administrator (optional)
                  </label>
                  <textarea
                    id="accessMessage"
                    className="input min-h-[80px]"
                    placeholder="Hi, I'm from the marketing team and would like access to Calculom..."
                    value={accessMessage}
                    onChange={(e) => setAccessMessage(e.target.value)}
                    maxLength={500}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {accessMessage.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isPending}
                  className="btn-primary w-full"
                >
                  {requestAccessMutation.isPending ? 'Submitting request...' : 'Request access'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleCreateNewOrg}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Or create a new organization instead
                  </button>
                </div>
              </>
            )}

            {/* Registration Mode */}
            {mode === 'register' && (
              <>
                <div>
                  <label htmlFor="tenantName" className="label">
                    Organization name
                  </label>
                  <input
                    id="tenantName"
                    type="text"
                    className="input"
                    placeholder="Acme Inc."
                    {...register('tenantName', {
                      required: 'Organization name is required',
                      minLength: { value: 2, message: 'Organization name must be at least 2 characters' },
                    })}
                  />
                  {errors.tenantName && (
                    <p className="mt-1 text-sm text-red-600">{errors.tenantName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="displayName" className="label">
                    Your name
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    className="input"
                    placeholder="John Smith"
                    {...register('displayName', { required: 'Your name is required' })}
                  />
                  {errors.displayName && (
                    <p className="mt-1 text-sm text-red-600">{errors.displayName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="label">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    className="input"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: 'Password must contain uppercase, lowercase, and a number',
                      },
                    })}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirm password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    className="input"
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) => value === password || 'Passwords do not match',
                    })}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isPending}
                  className="btn-primary w-full"
                >
                  {registerMutation.isPending ? 'Creating account...' : 'Create account'}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  By creating an account, you agree to our Terms of Service and Privacy Policy.
                </p>
              </>
            )}
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
